// src/pages/PhoneBlastPage.jsx
// Phone Number Announcement Blast - Send new phone number to all clients

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Phone, Send, CheckCircle2, AlertTriangle, Users, Search,
  ChevronLeft, RefreshCw, Loader2, Edit3, Check, X, MessageSquare,
  Clock, UserCheck, PhoneOff, Radio, Trash2, SkipForward
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '';

export default function PhoneBlastPage() {
  const navigate = useNavigate();

  // State
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [customMessages, setCustomMessages] = useState({}); // clientId -> custom message
  const [editingClientId, setEditingClientId] = useState(null);
  const [template, setTemplate] = useState('');
  const [editingTemplate, setEditingTemplate] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [clientsRes, templateRes, campaignsRes] = await Promise.all([
        fetch(`${API_BASE}/phone-blast/clients`, { credentials: 'include' }),
        fetch(`${API_BASE}/phone-blast/template`, { credentials: 'include' }),
        fetch(`${API_BASE}/phone-blast/campaigns`, { credentials: 'include' })
      ]);

      if (clientsRes.ok) {
        const data = await clientsRes.json();
        setClients(data.clients || []);
      }

      if (templateRes.ok) {
        const data = await templateRes.json();
        setTemplate(data.template?.content || '');
      }

      if (campaignsRes.ok) {
        const data = await campaignsRes.json();
        setCampaigns(data.campaigns || []);
      }
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filtered clients based on search
  const filteredClients = useMemo(() => {
    if (!searchQuery.trim()) return clients;
    const q = searchQuery.toLowerCase();
    return clients.filter(c =>
      c.firstName?.toLowerCase().includes(q) ||
      c.lastName?.toLowerCase().includes(q) ||
      c.phone?.includes(q)
    );
  }, [clients, searchQuery]);

  // Selection handlers
  const toggleSelect = (id) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  const selectAll = () => {
    setSelectedIds(new Set(filteredClients.map(c => c.id)));
  };

  const deselectAll = () => {
    setSelectedIds(new Set());
  };

  // Custom message handlers
  const setCustomMessage = (clientId, message) => {
    setCustomMessages(prev => ({
      ...prev,
      [clientId]: message
    }));
  };

  const clearCustomMessage = (clientId) => {
    setCustomMessages(prev => {
      const newMessages = { ...prev };
      delete newMessages[clientId];
      return newMessages;
    });
  };

  // Save template
  const saveTemplate = async () => {
    try {
      const res = await fetch(`${API_BASE}/phone-blast/template`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: template })
      });

      if (res.ok) {
        setEditingTemplate(false);
        setSuccess('Template saved');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError('Failed to save template');
    }
  };

  // Delete campaign
  const deleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) {
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/phone-blast/campaigns/${campaignId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        setCampaigns(prev => prev.filter(c => c.id !== campaignId));
        setSuccess('Campaign deleted');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to delete campaign');
      }
    } catch (err) {
      setError('Failed to delete campaign');
    }
  };

  // Mark campaign as complete (for stuck campaigns)
  const markCampaignComplete = async (campaignId) => {
    try {
      const res = await fetch(`${API_BASE}/phone-blast/campaigns/${campaignId}/complete`, {
        method: 'POST',
        credentials: 'include'
      });

      if (res.ok) {
        setCampaigns(prev => prev.map(c =>
          c.id === campaignId ? { ...c, status: 'completed', completed_at: new Date().toISOString() } : c
        ));
        setSuccess('Campaign marked as complete');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to complete campaign');
      }
    } catch (err) {
      setError('Failed to complete campaign');
    }
  };

  // Send blast
  const sendBlast = async () => {
    if (selectedIds.size === 0) {
      setError('Please select at least one client');
      return;
    }

    if (!template.trim()) {
      setError('Please enter a message template');
      return;
    }

    if (!window.confirm(`Are you sure you want to send this phone blast to ${selectedIds.size} client${selectedIds.size === 1 ? '' : 's'}? This cannot be undone.`)) {
      return;
    }

    setSending(true);
    setError(null);

    try {
      // Build recipients array with custom messages
      const recipients = Array.from(selectedIds).map(clientId => ({
        clientId,
        customMessage: customMessages[clientId] || null
      }));

      const res = await fetch(`${API_BASE}/phone-blast/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          recipients,
          templateContent: template
        })
      });

      const data = await res.json();

      if (res.ok && data.ok) {
        setSuccess(`Phone blast started for ${selectedIds.size} clients! Messages will be sent over the next few minutes.`);
        setSelectedIds(new Set());
        setCustomMessages({});
        fetchData(); // Refresh campaigns
      } else {
        setError(data.error || 'Failed to send blast');
      }
    } catch (err) {
      setError('Failed to send blast');
    } finally {
      setSending(false);
    }
  };

  // Format phone for display
  const formatPhone = (phone) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length === 11 && digits[0] === '1') {
      return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    return phone;
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
    });
  };

  // Status badge for campaigns
  const StatusBadge = ({ status }) => {
    const styles = {
      completed: 'bg-green-100 text-green-700',
      in_progress: 'bg-blue-100 text-blue-700',
      pending: 'bg-yellow-100 text-yellow-700',
      cancelled: 'bg-gray-100 text-gray-500'
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100'}`}>
        {status?.replace('_', ' ')}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-gray-100 to-slate-200">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/settings')}
            className="p-2 hover:bg-white rounded-lg transition"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">New Phone Number Blast</h1>
            <p className="text-slate-500 text-sm">Announce your new work phone number to clients</p>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
            <AlertTriangle className="text-red-500 flex-shrink-0" size={20} />
            <span className="text-red-700">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto">
              <X size={18} className="text-red-400" />
            </button>
          </div>
        )}

        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
            <CheckCircle2 className="text-green-500 flex-shrink-0" size={20} />
            <span className="text-green-700">{success}</span>
            <button onClick={() => setSuccess(null)} className="ml-auto">
              <X size={18} className="text-green-400" />
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Template & Actions */}
          <div className="space-y-6">
            {/* Template Editor */}
            <div className="bg-white rounded-2xl shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                  <MessageSquare size={18} />
                  Message Template
                </h2>
                {!editingTemplate ? (
                  <button
                    onClick={() => setEditingTemplate(true)}
                    className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                  >
                    <Edit3 size={14} /> Edit
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button onClick={saveTemplate} className="text-green-600 hover:bg-green-50 p-1 rounded">
                      <Check size={18} />
                    </button>
                    <button onClick={() => setEditingTemplate(false)} className="text-red-600 hover:bg-red-50 p-1 rounded">
                      <X size={18} />
                    </button>
                  </div>
                )}
              </div>

              {editingTemplate ? (
                <textarea
                  value={template}
                  onChange={(e) => setTemplate(e.target.value)}
                  className="w-full h-28 sm:h-40 p-3 border border-gray-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter your announcement message..."
                />
              ) : (
                <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-600 whitespace-pre-wrap min-h-[100px]">
                  {template || 'No template set'}
                </div>
              )}

              <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-700 font-medium mb-1">Available Variables:</p>
                <p className="text-xs text-blue-600">
                  {'{ClientFirstName}'} {'{AgentName}'} {'{AgentPhone}'} {'{AgencyName}'}
                </p>
              </div>
            </div>

            {/* Send Button */}
            <button
              onClick={sendBlast}
              disabled={sending || selectedIds.size === 0}
              className="w-full bg-[#FFB800] hover:bg-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed text-[#172A3A] font-bold py-4 rounded-xl transition flex items-center justify-center gap-2 shadow-lg"
            >
              {sending ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={20} />
                  Send Phone Blast ({selectedIds.size} selected)
                </>
              )}
            </button>

            {/* Recent Campaigns */}
            {campaigns.length > 0 && (
              <div className="bg-white rounded-2xl shadow p-6">
                <h2 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Clock size={18} />
                  Recent Campaigns
                </h2>
                <div className="space-y-3">
                  {campaigns.slice(0, 5).map(c => (
                    <div key={c.id} className="p-3 bg-slate-50 rounded-lg relative group">
                      <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {c.status === 'in_progress' && (
                          <button
                            onClick={() => markCampaignComplete(c.id)}
                            className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded"
                            title="Mark as complete"
                          >
                            <CheckCircle2 size={14} />
                          </button>
                        )}
                        <button
                          onClick={() => deleteCampaign(c.id)}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                          title="Delete campaign"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                      <div className="flex items-center justify-between mb-2 pr-8">
                        <span className="text-sm font-medium text-slate-700">
                          {c.total_recipients} clients
                        </span>
                        <StatusBadge status={c.status} />
                      </div>
                      <div className="grid grid-cols-3 sm:grid-cols-5 gap-1 text-xs">
                        <div className="flex items-center gap-1 text-green-600" title="Sent">
                          <CheckCircle2 size={12} />
                          {c.sent_count || 0}
                        </div>
                        <div className="flex items-center gap-1 text-blue-600" title="Replied">
                          <UserCheck size={12} />
                          {c.responded_count || 0}
                        </div>
                        <div className="flex items-center gap-1 text-gray-500" title="Skipped (cooldown)">
                          <SkipForward size={12} />
                          {c.skipped_count || 0}
                        </div>
                        <div className="flex items-center gap-1 text-red-600" title="Bad phone">
                          <PhoneOff size={12} />
                          {c.bad_phone_count || 0}
                        </div>
                        <div className="flex items-center gap-1 text-orange-600" title="Landline">
                          <Radio size={12} />
                          {c.landline_count || 0}
                        </div>
                      </div>
                      <p className="text-xs text-slate-400 mt-2">
                        {formatDate(c.started_at || c.created_at)}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="mt-3 text-xs text-slate-400 flex items-center flex-wrap gap-3">
                  <span className="flex items-center gap-1"><CheckCircle2 size={10} /> Sent</span>
                  <span className="flex items-center gap-1"><UserCheck size={10} /> Replied</span>
                  <span className="flex items-center gap-1"><SkipForward size={10} /> Skipped</span>
                  <span className="flex items-center gap-1"><PhoneOff size={10} /> Bad #</span>
                  <span className="flex items-center gap-1"><Radio size={10} /> Landline</span>
                </div>
              </div>
            )}
          </div>

          {/* Right Column: Client List */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow overflow-hidden">
            {/* Search & Select All */}
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center gap-4 mb-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search clients..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <button onClick={fetchData} className="p-2 hover:bg-gray-100 rounded-lg" title="Refresh">
                  <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    onClick={selectAll}
                    className="text-sm text-blue-600 hover:underline"
                  >
                    Select All ({filteredClients.length})
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    onClick={deselectAll}
                    className="text-sm text-gray-500 hover:underline"
                  >
                    Deselect All
                  </button>
                </div>
                <span className="text-sm text-slate-500">
                  {selectedIds.size} of {clients.length} selected
                </span>
              </div>
            </div>

            {/* Client List */}
            <div className="max-h-[600px] overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="animate-spin text-slate-400" size={32} />
                </div>
              ) : filteredClients.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <Users size={40} className="mx-auto mb-3 opacity-50" />
                  <p>No clients with phone numbers found</p>
                </div>
              ) : (
                filteredClients.map(client => {
                  const isSelected = selectedIds.has(client.id);
                  const hasCustomMessage = !!customMessages[client.id];
                  const isEditing = editingClientId === client.id;

                  return (
                    <div
                      key={client.id}
                      className={`border-b border-gray-50 transition ${
                        isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      {/* Main Row */}
                      <div
                        className="flex items-center gap-4 p-4 cursor-pointer"
                        onClick={() => toggleSelect(client.id)}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900">
                              {client.firstName} {client.lastName}
                            </span>
                            {client.riskScore >= 50 && (
                              <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                                Risk: {client.riskScore}
                              </span>
                            )}
                            {hasCustomMessage && (
                              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                                Custom
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <Phone size={14} />
                              {formatPhone(client.phone)}
                            </span>
                            {client.carrier && (
                              <span className="truncate max-w-[120px]">{client.carrier}</span>
                            )}
                          </div>
                        </div>

                        {/* Edit custom message button */}
                        {isSelected && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingClientId(isEditing ? null : client.id);
                            }}
                            className={`p-2 rounded-lg transition ${
                              isEditing ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-200 text-gray-500'
                            }`}
                            title="Customize message for this client"
                          >
                            <Edit3 size={16} />
                          </button>
                        )}

                        {isSelected && (
                          <CheckCircle2 className="text-blue-500 flex-shrink-0" size={20} />
                        )}
                      </div>

                      {/* Custom Message Editor */}
                      {isEditing && isSelected && (
                        <div className="px-4 pb-4">
                          <div className="bg-slate-50 rounded-lg p-3">
                            <div className="flex items-center justify-between mb-2">
                              <label className="text-xs font-medium text-slate-600">
                                Custom message for {client.firstName}:
                              </label>
                              {hasCustomMessage && (
                                <button
                                  onClick={() => clearCustomMessage(client.id)}
                                  className="text-xs text-red-500 hover:underline"
                                >
                                  Use default template
                                </button>
                              )}
                            </div>
                            <textarea
                              value={customMessages[client.id] || template}
                              onChange={(e) => setCustomMessage(client.id, e.target.value)}
                              placeholder="Enter custom message..."
                              className="w-full h-24 p-2 border border-gray-200 rounded text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <p className="text-xs text-slate-400 mt-1">
                              Variables will be replaced: {'{ClientFirstName}'} = {client.firstName}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
